<template>
  <div class="dashboard-header">
    <h1 class="dashboard-title">{{ title }}</h1>

    <div class="context-selectors">
      <div class="selector-group">
        <label class="selector-label">🏢 Cantine</label>
        <select v-model="canteenId" class="canteen-selector" @change="onCanteenChange">
          <option v-for="canteen in availableCanteens" :key="canteen.id" :value="canteen.id">
            {{ canteen.name }}
          </option>
        </select>
      </div>

      <div class="selector-group">
        <label class="selector-label">📅 Date</label>
        <input v-model="selectedDate" type="date" class="date-selector" @change="emitContext" />
      </div>

      <div class="selector-group">
        <label class="selector-label">🍽️ Service</label>
        <select
          v-model="selectedService"
          class="service-selector"
          @change="emitContext"
          :disabled="!canteenId || availableServices.length === 0"
        >
          <option v-if="availableServices.length === 0" disabled value="">Chargement...</option>
          <option v-for="service in availableServices" :key="service.id" :value="service.id">
            {{ service.name }} - {{ service.startTime.substring(0, 5) }}-{{ service.endTime.substring(0, 5) }}
          </option>
        </select>
        <small v-if="availableServices.length === 0 && canteenId" class="service-debug">
          ⚠️ Mode hors ligne - Services par défaut utilisés
        </small>
      </div>
    </div>

    <slot name="extra" />
  </div>
</template>

<script>
export default {
  name: "DashboardContextHeader",

  props: {
    title: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      effectifVariant: "SIMPLE",
      wasteVariant: "barquette",
      featureEffectifs: true,
      featureCommunications: true,
      featureWaste: true,
      featureVotes: true,
      availableCanteens: [],
      availableServices: [],
    }
  },

  async mounted() {
    this.loadAvailableCanteens()
    this.canteenId = this.getCanteenId()
    if (this.canteenId) {
      await this.loadAvailableServices()
    }
    this.emitContext()
  },

  methods: {
    getCanteenId() {
      if (this.$store.state.loggedUser && this.$store.state.loggedUser.canteen) {
        return this.$store.state.loggedUser.canteen.id
      }
      if (this.$store.state.userCanteenPreviews && this.$store.state.userCanteenPreviews.length > 0) {
        return this.$store.state.userCanteenPreviews[0].id
      }
      if (this.availableCanteens.length > 0) {
        return this.availableCanteens[0].id
      }
      return 1
    },

    loadAvailableCanteens() {
      if (this.$store.state.userCanteenPreviews && this.$store.state.userCanteenPreviews.length > 0) {
        this.availableCanteens = this.$store.state.userCanteenPreviews
      } else {
        this.availableCanteens = [{ id: 1, name: "Cantine par défaut" }]
      }
    },

    async onCanteenChange() {
      this.availableServices = []
      this.selectedService = null
      if (this.canteenId) {
        await this.loadAvailableServices()
      }
      this.emitContext()
    },

    async loadAvailableServices() {
      if (!this.canteenId) return
      try {
        const [servicesResp, canteenResp] = await Promise.all([
          fetch(`/api/v1/canteen/${this.canteenId}/services/`, { credentials: "same-origin" }),
          this.$store.dispatch("fetchCanteen", { id: this.canteenId }),
        ])
        if (servicesResp.ok) {
          this.availableServices = await servicesResp.json()
          this.selectedService = this.getDefaultService()
        } else {
          throw new Error(`HTTP ${servicesResp.status}`)
        }
        this.effectifVariant = canteenResp.effectifVariant || "SIMPLE"
        this.wasteVariant = canteenResp.wasteVariant || "barquette"
        this.featureEffectifs = canteenResp.showEffectifs ?? true
        this.featureCommunications = canteenResp.showCommunications ?? true
        this.featureWaste = canteenResp.showWaste ?? true
        this.featureVotes = canteenResp.showVotes ?? true
      } catch {
        this.availableServices = []
        this.selectedService = null
      }
    },

    getDefaultService() {
      if (this.availableServices.length === 0) return null
      const hour = new Date().getHours()
      const match = this.availableServices.find((s) => {
        const start = parseInt(s.startTime.split(":")[0])
        const end = parseInt(s.endTime.split(":")[0])
        return hour >= start && hour <= end
      })
      return match ? match.id : this.availableServices[0].id
    },

    emitContext() {
      this.$emit("context-change", {
        canteenId: this.canteenId,
        selectedDate: this.selectedDate,
        selectedService: this.selectedService,
        availableServices: this.availableServices,
        effectifVariant: this.effectifVariant,
        wasteVariant: this.wasteVariant,
        featureEffectifs: this.featureEffectifs,
        featureCommunications: this.featureCommunications,
        featureWaste: this.featureWaste,
        featureVotes: this.featureVotes,
      })
    },
  },
}
</script>

<style scoped>
.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.context-selectors {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 1.5rem 0;
  flex-wrap: wrap;
}

.selector-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.selector-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.date-selector,
.service-selector,
.canteen-selector {
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  background: white;
  color: #1a1a1a;
  transition: all 0.2s;
  min-width: 180px;
}

.date-selector:focus,
.service-selector:focus,
.canteen-selector:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.service-selector:disabled,
.canteen-selector:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.service-selector,
.canteen-selector {
  cursor: pointer;
}

.service-debug {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #f59e0b;
  font-weight: 500;
}
</style>
